/**
 * Componente Button — GUIA_REACT_v2 §2.7.
 *
 * Variantes basadas en class-variance-authority. Regla de seguridad amarillo:
 * la variante `accent` siempre lleva texto azul.
 */
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../../utils/cn';

// eslint-disable-next-line react-refresh/only-export-components -- variants se reusan en anchor tags como CTA.
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-[10px] font-body font-bold tracking-[0.02em] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        primary:
          'bg-etribe-blue text-white hover:-translate-y-0.5 hover:shadow-brand active:translate-y-0 focus-visible:ring-etribe-blue',
        accent:
          'bg-etribe-yellow text-etribe-blue hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,200,54,.5)] focus-visible:ring-etribe-yellow',
        secondary:
          'bg-etribe-purple text-white hover:bg-etribe-purple-dark focus-visible:ring-etribe-purple',
        outline:
          'border-2 border-etribe-blue text-etribe-blue bg-transparent hover:bg-etribe-blue-50 focus-visible:ring-etribe-blue',
        'outline-white':
          'border-2 border-white/40 text-white bg-transparent hover:bg-white/10 focus-visible:ring-white',
        ghost:
          'text-etribe-blue bg-transparent hover:bg-etribe-blue-50 focus-visible:ring-etribe-blue',
        destructive:
          'bg-etribe-red text-white hover:opacity-90 focus-visible:ring-etribe-red',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-11 px-5 text-sm',
        lg: 'h-14 px-7 text-[15px]',
        xl: 'h-16 px-8 text-base',
      },
      fullWidth: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, fullWidth, isLoading, leftIcon, rightIcon, className, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? <Spinner /> : leftIcon}
      {children}
      {!isLoading && rightIcon}
    </button>
  ),
);

Button.displayName = 'Button';

function Spinner() {
  return (
    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <circle cx="12" cy="12" r="9" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
    </svg>
  );
}
